import { Injectable } from '@angular/core';
import { OrganizationService } from './organization.service';
import * as pdfjsLib from 'pdfjs-dist';
import * as Parse from 'parse';

export type DocumentCategory = 'salesComps2' |
                               'listingDocument2' |
                               'acquisionContract2' |
                               'resaleContract2' |
                               'addendum' |
                               'other' |
                               'closing';

@Injectable()
export class DocumentService {

    constructor(private orgService: OrganizationService) {
    }

    async loadDocumentsByCategory(property: Parse.Object, category: DocumentCategory) {
        const query: Parse.Query = new Parse.Query('Document');

        query.equalTo('property', property);
        query.equalTo('category', category);
        query.equalTo('organization', this.orgService.organization);
        query.descending('createdAt');

        return query.find();
    }

    async createDocument(file: File, property: Parse.Object, category: DocumentCategory) {
        const parseFile: Parse.File = new Parse.File(file.name.replace(/[^a-zA-Z0-9]/g,'_'), file);
        const document: Parse.Object = new Parse.Object('Document');

        document.set('file', parseFile);
        document.set('name', file.name);
        document.set('property', property);
        document.set('category', category);

        try {
            // Generate thumbnail
            if (file.type === 'application/pdf') {
                const thubmnailBase64 = await this.generatePDFThumbnail(file);

                if (thubmnailBase64) {
                    const thumbnail: Parse.File = new Parse.File('thumb.png', { base64: thubmnailBase64 });
                    document.set('thumbnail', thumbnail);
                }
            } else {
                document.set('thumbnail', null);
            }
        } catch (error) {
            console.error(error);
            // Thumbnail creation can fail silently
        }

        if (category !== 'salesComps2' && category !== 'listingDocument2' && category !== 'acquisionContract2' && category !== 'resaleContract2') {
            return document.save();
        }

        await document.save();

        // delete the old document if exists
        if (property.has(category)) {
            await property.get(category).destroy();
        }

        property.set(category, document);

        return property.save();
    }

    async generatePDFThumbnail(file: File, width = 400) {
        try {
            pdfjsLib.GlobalWorkerOptions.workerSrc = '//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.js';

            const base64 = await this.getFileBinary(file);
            const pdfData = atob(base64);

            const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
            const page = await pdf.getPage(1);

            const desiredWidth = width;
            const viewport = page.getViewport({ scale: 1 });
            const scale = desiredWidth / viewport.width;
            const scaledViewport = page.getViewport({ scale });

            // Prepare canvas using PDF page dimensions
            const canvas = document.createElement('canvas');

            const context = canvas.getContext('2d');
            canvas.height = scaledViewport.height;
            canvas.width = scaledViewport.width;

            // Render PDF page into canvas context
            const renderContext = {
                canvasContext: context as CanvasRenderingContext2D,
                viewport: scaledViewport
            };

            await page.render(renderContext).promise;

            return canvas.toDataURL();
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    async getFileBinary(file: File): Promise<string> {
        return new Promise( (resolve) => {
            const reader = new FileReader();

            reader.onload = () => {
                resolve((reader.result as string).replace('data:application/pdf;base64,', ''));
            };

            reader.readAsDataURL(file);
        });
    }
}
